import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { fetchCouponUsagesByCouponId } from "../../shared/commerceMastersApi";
import { IconList, IconRefresh } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatDateTime(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

export function CouponUsagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const couponParam = searchParams.get("coupon");
  const couponId = Number(couponParam);

  const [couponMeta, setCouponMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const coupon =
    Boolean(couponParam) && Number.isFinite(couponId) && couponId > 0 ? couponId : null;

  const customerById = useMemo(() => {
    const map = new Map();
    for (const customer of customers) map.set(customer.id, customer);
    return map;
  }, [customers]);

  const orderById = useMemo(() => {
    const map = new Map();
    for (const order of orders) map.set(order.id, order);
    return map;
  }, [orders]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        usage: item,
        customerName: customerById.get(item.customerId)?.name ?? `#${item.customerId}`,
        orderNo: orderById.get(item.orderId)?.orderNo ?? `#${item.orderId}`,
        amount: item.amount ?? 0,
        date: formatDateTime(item.date)
      })),
    [items, customerById, orderById]
  );

  useEffect(() => {
    if (searchParams.get("edit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    if (!coupon) return;
    setLoading(true);
    setError(null);
    const [cRes, oRes, coRes, uRes] = await Promise.all([
      apiGet("/api/Customers"),
      apiGet("/api/Orders"),
      apiGet(`/api/Coupons/${coupon}`),
      fetchCouponUsagesByCouponId(coupon)
    ]);
    if (cRes.ok) setCustomers(cRes.data ?? []);
    if (oRes.ok) setOrders(oRes.data ?? []);
    if (coRes.ok) setCouponMeta(coRes.data);
    if (!uRes.ok) {
      setError(uRes.error ?? "Failed to load coupon usages");
      setItems([]);
    } else {
      setItems(uRes.items);
    }
    setLoading(false);
  }, [coupon]);

  useEffect(() => {
    void load();
  }, [load]);

  const couponLabel = couponMeta?.code ?? couponMeta?.title ?? (coupon != null ? `Coupon #${coupon}` : "");
  const total = rows.length;

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Id", width: 90 },
      { field: "date", headerName: "Date", minWidth: 170, flex: 0.9 },
      { field: "customerName", headerName: "Customer", minWidth: 140, flex: 1 },
      { field: "orderNo", headerName: "Order", minWidth: 120, flex: 0.8 },
      { field: "amount", headerName: "Amount", width: 110 }
    ],
    []
  );

  if (!coupon) {
    return <Navigate to="/coupons-list" replace />;
  }

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Coupon usage</h2>
          <p>
            {couponLabel}
            {loading ? "" : ` · ${total} record${total === 1 ? "" : "s"} ·`}
          </p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/coupons-list" className="icon-button icon-button--toolbar" title="Coupons list" aria-label="Back to coupons list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
